/**
 * Percent error and percent difference — two lab formulas that are easy to mix up.
 *
 * Percent error compares a measurement with an accepted (true, theoretical, literature)
 * value, so the accepted value is the denominator. Percent difference compares two
 * measurements when neither is "right", so the denominator is their average.
 */

export interface PercentErrorResult {
  experimental: number;
  accepted: number;
  /** E − A: positive when the measurement is above the accepted value. */
  difference: number;
  /** |E − A| */
  absoluteError: number;
  /** |E − A| / |A| */
  relativeError: number;
  /** |E − A| / |A| × 100 */
  percentError: number;
  /** (E − A) / |A| × 100 — the sign says above (+) or below (−). */
  signedPercentError: number;
}

/** Null when the accepted value is zero: the relative error is undefined. */
export function percentError(
  experimental: number,
  accepted: number
): PercentErrorResult | null {
  if (accepted === 0) {
    return null;
  }
  const difference = experimental - accepted;
  const absoluteError = Math.abs(difference);
  const relativeError = absoluteError / Math.abs(accepted);
  return {
    experimental,
    accepted,
    difference,
    absoluteError,
    relativeError,
    percentError: relativeError * 100,
    signedPercentError: (difference / Math.abs(accepted)) * 100,
  };
}

export interface PercentDifferenceResult {
  first: number;
  second: number;
  /** |a − b| */
  absoluteDifference: number;
  /** (|a| + |b|) / 2 — the plain average when both values are positive. */
  average: number;
  /** |a − b| / average × 100, always between 0 and 200. */
  percentDifference: number;
}

/** Null when both values are zero: there is no average to compare against. */
export function percentDifference(
  first: number,
  second: number
): PercentDifferenceResult | null {
  const average = (Math.abs(first) + Math.abs(second)) / 2;
  if (average === 0) {
    return null;
  }
  const absoluteDifference = Math.abs(first - second);
  return {
    first,
    second,
    absoluteDifference,
    average,
    percentDifference: (absoluteDifference / average) * 100,
  };
}
